import React, { useState, useEffect } from 'react';
import { useFeedback } from '../context/FeedbackContext';
import { apiService } from '../services/api';
import styles from '../styles/feedbackModal.module.css';

interface FeedbackModalProps {
  userEmail: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ userEmail }) => {
  const { isFeedbackModalOpen, closeFeedbackModal, currentLocation, deviceInfo } = useFeedback();
  const [feedbackType, setFeedbackType] = useState<'General' | 'Error / Bug' | 'Idea'>('General');
  const [feedbackText, setFeedbackText] = useState('');
  const [emailCopyToUser, setEmailCopyToUser] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isFeedbackModalOpen) {
      setFeedbackType('General');
      setFeedbackText('');
      setEmailCopyToUser(false);
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [isFeedbackModalOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apiService.submitFeedback({
        userEmail,
        locationInApp: currentLocation,
        feedbackType,
        feedbackText: feedbackText.trim(),
        deviceInfo,
        emailCopyToUser,
      });

      setSubmitSuccess(true);

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !feedbackText.trim() || isSubmitting;

  if (!isFeedbackModalOpen) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={closeFeedbackModal}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerTop}>
            <h2 className={styles.modalTitle}>Feedback Form</h2>
            <button
              type="button"
              className={styles.closeButton}
              onClick={closeFeedbackModal}
              aria-label="Close feedback form"
            >
              ×
            </button>
          </div>
          <p className={styles.modalSubtitle}>Thank you for helping to improve Recovery Commons</p>
        </div>

        {submitSuccess ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>✓</div>
            <h3>Thank you!</h3>
            <p>Your feedback has been submitted successfully.</p>
            {emailCopyToUser && (
              <p className={styles.emailConfirmation}>
                A copy has been emailed to {userEmail}
              </p>
            )}
            <button 
              onClick={closeFeedbackModal}
              className={styles.okButton}
            >
              Ok
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.feedbackForm}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Type of feedback:</label>
              <div className={styles.radioGroup}>
                {(['General', 'Error / Bug', 'Idea'] as const).map((type) => (
                  <label key={type} className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="feedbackType"
                      value={type}
                      checked={feedbackType === type}
                      onChange={(e) => setFeedbackType(e.target.value as typeof feedbackType)}
                      className={styles.radioInput}
                    />
                    <span className={styles.radioText}>{type}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label} htmlFor="feedbackText">
                Feedback:
              </label>
              <textarea
                id="feedbackText"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Please share your feedback..."
                className={styles.textarea}
                rows={4}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={emailCopyToUser}
                  onChange={(e) => setEmailCopyToUser(e.target.checked)}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>Email me a copy of my feedback</span>
              </label>
            </div>

            {submitError && (
              <div className={styles.errorMessage}>
                {submitError}
              </div>
            )}

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={closeFeedbackModal}
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.submitButton} ${isSubmitDisabled ? styles.disabled : ''}`}
                disabled={isSubmitDisabled}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>

            <div className={styles.readOnlySection}>
              <div className={styles.compactInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>User:</span>
                  <span className={styles.infoValue}>{userEmail}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Location in app:</span>
                  <span className={styles.infoValue}>{currentLocation}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Device & Browser:</span>
                  <span className={styles.infoValue}>
                    {`${deviceInfo.deviceType} - ${deviceInfo.browser} ${deviceInfo.browserVersion} on ${deviceInfo.operatingSystem}`}
                  </span>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal; 